import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ConnectionError from '../../../components/ConnectionError/ConnectionError';
import EmptyState from '../../../components/EmptyState/EmptyState';
import { adminService } from '../../../services/adminService';
import type { ContactMessage } from '../../../types';
import { ConfirmationModal } from '../../../components/common/ConfirmationModal/ConfirmationModal';
import { MessageList } from '../../../components/dashboard/messages/MessageList';
import { useScrollPagination } from '../../../hooks/useScrollPagination';
import { useShowScrollTop } from '../../../hooks/useShowScrollTop';
import './MessagesPage.css';

function MessagesPage() {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // Soft Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<string | null>(null);

  // Reusable scroll to top hook
  const { showScrollTop, scrollToTop } = useShowScrollTop(300);

  // Initial Load
  const loadMessages = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await adminService.getContactMessages(0, 10);
      setMessages(response.content);
      setPage(0);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load More (Infinite Scroll)
  const loadMoreMessages = useCallback(async () => {
    if (isLoadingMore || page >= totalPages - 1) return;
    setIsLoadingMore(true);
    try {
      const nextPage = page + 1;
      const response = await adminService.getContactMessages(nextPage, 10);
      setMessages((prev) => [...prev, ...response.content]);
      setPage(nextPage);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch {
      toast.error(t('errors.loadFailed') || 'Failed to load more messages');
    } finally {
      setIsLoadingMore(false);
    }
  }, [page, totalPages, isLoadingMore, t]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Reusable scroll pagination hook
  useScrollPagination({
    loadMore: loadMoreMessages,
    hasMore: page < totalPages - 1,
    isLoading: loading || isLoadingMore,
    threshold: 200,
  });

  const handleDeleteClick = (id: string) => {
    setMessageToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!messageToDelete) return;
    try {
      await adminService.deleteContactMessage(messageToDelete);
      toast.success(t('admin.deleteMessageSuccess'));
      setMessages((prev) => prev.filter((msg) => msg.id !== messageToDelete));
      setTotalElements((prev) => Math.max(0, prev - 1));
    } catch {
      toast.error(t('admin.deleteMessageError'));
    } finally {
      setDeleteModalOpen(false);
      setMessageToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <span className="auth-spinner auth-spinner-brand"></span>
      </div>
    );
  }

  if (error) {
    return <ConnectionError onRetry={loadMessages} />;
  }

  return (
    <div className="admin-messages-page">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">{t('admin.messages')}</h1>
          <p className="admin-page-subtitle">{t('admin.messagesDesc')}</p>
        </div>
        <span className="admin-badge">
          {totalElements} {t('admin.totalMessages')}
        </span>
      </div>

      {messages.length === 0 ? (
        <EmptyState
          icon={
            <svg
              viewBox="0 0 24 24"
              width="40"
              height="40"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          }
          title={t('admin.noMessages')}
          description={t('errors.noMessagesDesc')}
        />
      ) : (
        <MessageList messages={messages} onDelete={handleDeleteClick} />
      )}

      {/* Infinite Scroll loading indicator */}
      {isLoadingMore && (
        <div className="admin-messages-load-more">
          <span className="auth-spinner auth-spinner-brand"></span>
        </div>
      )}

      {/* Reusable Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        title={t('admin.deleteMessageConfirmTitle')}
        message={t('admin.deleteMessageConfirmDesc')}
        confirmLabel={t('admin.delete') || 'Delete'}
        cancelLabel={t('admin.cancel') || 'Cancel'}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
        type="danger"
      />

      {/* Cool floating Go to Top Button */}
      <button
        className={`admin-go-to-top ${showScrollTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Go to top"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
    </div>
  );
}

export default MessagesPage;
